import { useEffect, useState } from 'react';
import { missingChoice, toggleChoice, unitPrice } from '../lib/cart';
import { baht, main, second } from '../lib/text';
import type { MenuItem } from '../lib/types';
import { Back, Camera } from '../ui/icons';
import type { GuestState } from './GuestApp';

/** Dish close-up: the real photo with its sticker, options, quantity and a note for the kitchen. */
export function DishSheet({ item, s, onClose }: { item: MenuItem; s: GuestState; onClose: () => void }) {
  const [choices, setChoices] = useState<Record<string, string[]>>(() =>
    Object.fromEntries((item.options ?? []).map((g) => [g.id, g.min > 0 && g.max === 1 ? [g.choices[0].id] : []])));
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const missing = missingChoice(item, choices);
  const sold = s.soldOut.has(item.id);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="sheet-wrap" onClick={onClose}>
      <section className="sheet" role="dialog" aria-modal="true" aria-label={main(item.name)} onClick={(e) => e.stopPropagation()}>
        <div className="row">
          <button className="icon-btn" onClick={onClose} aria-label="Back to menu"><Back /></button>
          <span className="table-chip">{s.guest.tableName}</span>
        </div>

        {item.media?.photo && (
          <figure className="photo-card" style={{ margin: '4px 6px 24px' }}>
            <img className="real" src={item.media.photo} alt={`Real photo of ${main(item.name)}`} />
            <figcaption className="caption">
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Camera />รูปจริงจากครัว · Real photo</span>
            </figcaption>
            {item.media.sticker && <img className="stk" src={item.media.sticker} alt="" />}
          </figure>
        )}

        <div>
          <h2 className="display" style={{ margin: 0, fontSize: 26, lineHeight: 1.15 }}>{main(item.name)}</h2>
          <span className="muted" style={{ fontSize: 14 }}>{[second(item.name), baht(item.priceSatang)].filter(Boolean).join(' · ')}</span>
          {item.description && <p style={{ margin: '6px 0 0' }}>{main(item.description)}</p>}
        </div>

        {(item.options ?? []).map((g) => (
          <fieldset key={g.id} style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <legend className="label" style={{ marginBottom: 8 }}>
              {main(g.name)} <span className="muted" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>
                {g.min > 0 ? (g.max === 1 ? '· เลือก 1 · pick 1' : `· pick ${g.min}-${g.max}`) : g.max > 1 ? `· up to ${g.max}` : '· optional'}
              </span>
            </legend>
            <div className="choices">
              {g.choices.map((c) => {
                const on = (choices[g.id] ?? []).includes(c.id);
                return (
                  <button key={c.id} className="choice" aria-pressed={on} onClick={() => setChoices({ ...choices, [g.id]: toggleChoice(g, choices[g.id] ?? [], c.id) })}>
                    <span>{main(c.name)}</span>
                    {(second(c.name) || c.priceSatang > 0) && (
                      <small>{[second(c.name), c.priceSatang > 0 ? `+${baht(c.priceSatang)}` : ''].filter(Boolean).join(' · ')}</small>
                    )}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="label">หมายเหตุ · Note for the kitchen</span>
          <textarea className="note" rows={2} maxLength={140} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ไม่ใส่ถั่ว · no peanuts" />
        </label>

        <div className="row" style={{ gap: 10, marginTop: 4 }}>
          <div className="stepper">
            <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="One less">−</button>
            <span aria-live="polite">{qty}</span>
            <button onClick={() => setQty(Math.min(50, qty + 1))} aria-label="One more">+</button>
          </div>
          <button
            className="cta light"
            style={{ flex: 1 }}
            disabled={!!missing || sold}
            onClick={() => { s.add(item.id, choices, qty, note); onClose(); }}
          >
            {sold ? 'หมดแล้ว · Sold out' : missing ? `เลือก${main(missing.name)}` : `ใส่ถาด · Add · ${baht(unitPrice(item, choices) * qty)}`}
          </button>
        </div>
      </section>
    </div>
  );
}
